import React, {useState} from 'react';

import {assertAndContinue} from 'Asserts';
import {InsightsFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {
  AnalyticsDashboardActions,
  updateAnalyticsDashboard,
} from 'insights/analytics-dashboards/AnalyticsDashboardActions';
import * as MakeSpecRequest from 'insights/analytics-dashboards/data/MakeSpecRequest';
import AnalyticsDescriptionEditor from 'insights/AnalyticsDescriptionEditor';
import AnalyticsProjectSelector from 'insights/shared/components/AnalyticsProjectSelector';
import {getPartyDisplayString} from 'parties/GetPartyDisplayString';
import Col from 'ui-library/bs/Col';
import FormGroup from 'ui-library/bs/FormGroup';
import Row from 'ui-library/bs/Row';
import {ButtonStyle} from 'ui-library/buttons/Button';
import useFormInput from 'ui-library/hooks/useFormInput';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import TextField from 'ui-library/inputs/TextField';
import Modal from 'ui-library/modals/Modal';
import {readableTime} from 'util/DateUtil';
import fuzzydate from 'util/fuzzydate';
import {capitalize} from 'util/NameUtil';
import {withLoadingScreen} from 'util/Promises';

import {GetAnalyticsDashboard} from './graphql/__generated/GetAnalyticsDashboard';

type AnalyticsDashboard = ExtractByTypename<
  GetAnalyticsDashboard['analyticsDashboard'],
  'AnalyticsDashboardNode'
>;

export type Actions = Pick<AnalyticsDashboardActions, 'updateAnalyticsDashboard'>;

export interface AnalyticsDashboardSettingsModalProps {
  actions: Actions;
  analyticsDashboard: AnalyticsDashboard;
  onRequestClose: () => void;
  onRequestSubmit: () => void;
}

export function AnalyticsDashboardSettingsModal(props: AnalyticsDashboardSettingsModalProps): JSX.Element {
  const [description, setDescription] = useState<string>(props.analyticsDashboard.description);
  const nameFormInput = useFormInput(props.analyticsDashboard.name);
  const [projectId, setProjectId] = useState<string>(props.analyticsDashboard.project.id);

  async function handleSubmit(): Promise<void> {
    const dashboardSpecRequest = {
      ...MakeSpecRequest.onUpdateAnalyticsDashboard(props.analyticsDashboard),
      description,
      name: nameFormInput.value,
      projectId,
    };
    await withLoadingScreen(
      props.actions.updateAnalyticsDashboard(props.analyticsDashboard.id, dashboardSpecRequest),
      'Saving dashboard...'
    );
    props.onRequestSubmit();
  }

  assertAndContinue(
    isFeatureEnabled({feature: InsightsFeature.INSIGHTS_MOVE}),
    'Expected INSIGHTS_MOVE to be enabled if INSIGHTS_EDIT is enabled'
  );

  return (
    <Modal
      buttons={[{style: ButtonStyle.SECONDARY, submit: true, text: 'Save'}]}
      className="modal-text-only"
      onRequestClose={props.onRequestClose}
      onRequestSubmit={handleSubmit}
      title="Dashboard settings"
    >
      <Row>
        <Col sm={6}>
          <FormGroup>
            <TextField
              label="Name"
              dataTestAttrs={{component: 'AnalyticsDashboardSettingsModal', element: 'dashboardName'}}
              {...nameFormInput}
            />
          </FormGroup>
        </Col>
        <Col sm={6}>
          <FormGroup>
            <FieldLabel label="Project" />
            <AnalyticsProjectSelector onChange={setProjectId} projectId={projectId} />
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col sm={12}>
          <FormGroup>
            <FieldLabel label="Description" />
            <AnalyticsDescriptionEditor setDescription={setDescription} description={description} />
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col sm={6}>
          <FieldLabel label="Creator" />
          <p>{getPartyDisplayString(props.analyticsDashboard.creator)}</p>
        </Col>
        <Col sm={6}>
          <FieldLabel label="Last modified" />
          <p>{capitalize(fuzzydate(props.analyticsDashboard.modifiedAt))}</p>
        </Col>
      </Row>
      <Row>
        <Col sm={6}>
          <FieldLabel label="Created" />
          <p>{readableTime(props.analyticsDashboard.createdAt)}</p>
        </Col>
      </Row>
    </Modal>
  );
}

export default function ConnectedAnalyticsDashboardSettingsModal(
  props: Omit<AnalyticsDashboardSettingsModalProps, 'actions'>
): JSX.Element {
  return <AnalyticsDashboardSettingsModal {...props} actions={{updateAnalyticsDashboard}} />;
}
